import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'

// Read the user ID directly from the stored JWT so we can scope localStorage
// keys synchronously — before React auth state has a chance to hydrate.
function getUserIdSync(): number | null {
  try {
    const token = localStorage.getItem('stonks_token')
    if (!token) return null
    const payload = JSON.parse(atob(token.split('.')[1]))
    if (payload.exp && payload.exp * 1000 < Date.now()) return null
    return typeof payload.id === 'number' ? payload.id : null
  } catch { return null }
}

export type ViewMode = 'grid' | 'crate'

export interface SortOption {
  value: string
  label: string
}

export const SORT_OPTIONS: SortOption[] = [
  { value: 'elevated_score', label: 'Gem Score' },
  { value: 'match_score', label: 'Match Score' },
  { value: 'bayesian_rating', label: 'Rating (Bayesian)' },
  { value: 'age_adjusted_rating', label: 'Fresh Rating' },
  { value: 'want_have_ratio', label: 'Demand (Want/Have)' },
  { value: 'median', label: 'Median Price' },
  { value: 'want', label: 'Want Count' },
  { value: 'last_sold', label: 'Recently Sold' },
  { value: 'released', label: 'Release Date' },
  { value: 'title', label: 'Title (A-Z)' },
  { value: 'date', label: 'Date Added' },
]

// Map global sort keys to artist/label API sort fields
const ENTITY_SORT_MAP: Record<string, string> = {
  elevated_score: 'bayesian_elevated_score',
  match_score: 'bayesian_match_score',
  bayesian_rating: 'avg_rating',
  age_adjusted_rating: 'avg_age_adjusted_rating',
  want_have_ratio: 'total_want',
  median: 'bayesian_elevated_score',
  want: 'total_want',
  last_sold: 'bayesian_elevated_score',
  released: 'bayesian_elevated_score',
  title: 'name',
}

export function mapSortForEntity(sort: string): string {
  return ENTITY_SORT_MAP[sort] || 'bayesian_elevated_score'
}

interface ViewSortState {
  viewMode: ViewMode
  sort: string
  order: 'asc' | 'desc'
  setViewMode: (mode: ViewMode) => void
  setSort: (sort: string) => void
  setOrder: (order: 'asc' | 'desc') => void
  setSortWithOrder: (sort: string) => void
  toggleOrder: () => void
}

function sortStorageKey(userId: number | null): string {
  return userId ? `stonks_view_sort:${userId}` : 'stonks_view_sort:guest'
}

function loadState(): { viewMode: ViewMode; sort: string; order: 'asc' | 'desc' } {
  try {
    const stored = localStorage.getItem(sortStorageKey(getUserIdSync()))
    if (stored) {
      const parsed = JSON.parse(stored)
      return {
        viewMode: (parsed.viewMode === 'table' ? 'grid' : parsed.viewMode) || 'grid',
        sort: parsed.sort || 'bayesian_rating',
        order: parsed.order || 'desc',
      }
    }
  } catch {
    // ignore
  }
  return { viewMode: 'grid', sort: 'bayesian_rating', order: 'desc' }
}

function saveState(state: { viewMode: ViewMode; sort: string; order: 'asc' | 'desc' }) {
  try {
    localStorage.setItem(sortStorageKey(getUserIdSync()), JSON.stringify(state))
  } catch {
    // ignore
  }
}

const ViewSortContext = createContext<ViewSortState | null>(null)

export function ViewSortProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState(loadState)

  const setViewMode = useCallback((viewMode: ViewMode) => {
    setState(prev => {
      const next = { ...prev, viewMode }
      saveState(next)
      return next
    })
  }, [])

  const setSort = useCallback((sort: string) => {
    setState(prev => {
      const next = { ...prev, sort }
      saveState(next)
      return next
    })
  }, [])

  const setOrder = useCallback((order: 'asc' | 'desc') => {
    setState(prev => {
      const next = { ...prev, order }
      saveState(next)
      return next
    })
  }, [])

  const setSortWithOrder = useCallback((sort: string) => {
    setState(prev => {
      const order: 'asc' | 'desc' = sort === 'title' || sort === 'released' ? 'asc' : 'desc'
      const next = { ...prev, sort, order }
      saveState(next)
      return next
    })
  }, [])

  const toggleOrder = useCallback(() => {
    setState(prev => {
      const next = { ...prev, order: prev.order === 'desc' ? 'asc' as const : 'desc' as const }
      saveState(next)
      return next
    })
  }, [])

  return (
    <ViewSortContext.Provider value={{
      viewMode: state.viewMode,
      sort: state.sort,
      order: state.order,
      setViewMode,
      setSort,
      setOrder,
      setSortWithOrder,
      toggleOrder,
    }}>
      {children}
    </ViewSortContext.Provider>
  )
}

export function useViewSort() {
  const context = useContext(ViewSortContext)
  if (!context) {
    throw new Error('useViewSort must be used within a ViewSortProvider')
  }
  return context
}
